// CASH1-S1-U1 (docs/build-briefs/CASH1-S1-U1-drawer-screens.md; PRD P1.1; DEC-399; design/mockups/cash1-cash-and-drawers.html):
// the cash drawer screens over the CASH1-S1 routes. One scrim-backed sheet built at runtime (check:chrome-ratchet refuses a new
// overlay written into index.html), with one body per step: open with a counted float, the drawer menu, the blind count to close,
// the outside-the-limit step with its one recount, the reason and manager step, and the result.
//
// FLOOR surface, touch only. Money is never typed: every amount is built from steppers (cashDrawerModel.ts). Every gate here
// reads a permission id through the injected `has`, never a role name. The count is blind: Expected and the over or short
// appear only for a person holding drawer.view_expected, and only when the server sent them. All text via textContent.
//
// State lives in this module and nowhere else. reset() (called from sign-out) closes the sheet, clears the open drawer, the
// counts, the reasons and the cash flag, and drops any read still in flight.

import {
  closeCashDrawer,
  getCashTenderState,
  getCurrentCashDrawer,
  kickCashDrawer,
  listActionReasons,
  openCashDrawer,
  postCashDrawerMove,
  type ActionReasonDto,
  type ApiErrorBody,
  type ApiResult,
  type CashDrawerSessionDto,
} from "./api.ts";
import { renderDenominationGrid } from "./cashDenominationGrid.ts";
import {
  breakdownOf,
  canSaveMove,
  cashEnabledFrom,
  drawerKickFailedFrom,
  classifyCloseResponse,
  classifyMoveResponse,
  clockTime,
  countTitle,
  dollars,
  drawerEntryVisible,
  emptyCounts,
  floatLine,
  moveBody,
  overShort,
  totalCents,
  visibleMoves,
  type DrawerCounts,
  type DrawerMoveSpec,
} from "./cashDrawerModel.ts";

export type CashDrawerApprovalResult<T> =
  { canceled: true } | { canceled: false; result: ApiResult<T> };

export interface CashDrawerDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds this permission, outright or with a manager's approval (a gate for what to show). */
  has: (permissionId: string) => boolean;
  /** The session holds this permission outright, with no approval needed (hasPermission is also true for approval_required). */
  holdsOutright: (permissionId: string) => boolean;
  /** The existing manager-approval flow (main.ts requestApproval). */
  requestApproval: <T extends Partial<ApiErrorBody>>(
    actionLabel: string,
    attempt: (approval?: { approverUserId: string; approverPin: string }) => Promise<ApiResult<T>>,
    returnTo: () => void,
  ) => Promise<CashDrawerApprovalResult<T>>;
  /** Puts back the screen the sheet was opened over (the Shift hub). The approval screens replace it while they are up and do not
   * go away on their own after an approved attempt, so this runs after one and on cancel. */
  showOrigin: () => void;
  /** The Cash drawer row may have appeared or gone: redraw the menu rows. */
  onEntryChanged: () => void;
  toast: (message: string) => void;
}

export interface CashDrawerController {
  /** Reads this device's drawer and the location's cash switch. Safe to call often. */
  refreshEntry: () => Promise<void>;
  /** The Cash drawer row shows. False until a read succeeds. */
  entryVisible: () => boolean;
  /** Opens the sheet at the right step for this device's drawer. */
  openSheet: () => Promise<void>;
  /** CASH1-S2-U1. A fresh read for Pay: whether this location takes cash and whether this device has an open drawer. A failed
   * read (no permission, offline, no device) is `null`, which Pay treats as cash not offered. */
  tenderState: () => Promise<{ cashEnabled: boolean; drawerOpen: boolean; canOpen: boolean } | null>;
  /** CASH1-S2-U1. Taking cash, or refunding it, needs an open drawer on this device. Resolves true at once when one is open, otherwise runs the
   * open flow (counted float) and resolves true when the drawer opens, false when it is cancelled or cannot be read. */
  ensureOpenForTender: (options?: { refund?: boolean }) => Promise<boolean>;
  /** Sign-out: close the sheet and forget everything. */
  reset: () => void;
  isOpen: () => boolean;
}

type Step =
  "loading" | "open" | "menu" | "count" | "limit" | "approve" | "result" | "move" | "movecount";

const SCRIM_ID = "cash-drawer-scrim";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

function button(
  className: string,
  text: string,
  testId: string,
  onClick: () => void,
): HTMLButtonElement {
  const node = el("button", className, text, testId);
  node.type = "button";
  node.addEventListener("click", onClick);
  return node;
}

function messageOf(body: { message?: string } | undefined): string {
  const message = body?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

export function createCashDrawer(deps: CashDrawerDeps): CashDrawerController {
  /** Bumped by sign-out only: drops an entry read that returns after it. */
  let sessionGeneration = 0;
  /** Bumped by every sheet close: drops a sheet read or write that returns after it. */
  let generation = 0;
  let known = false;
  let cashEnabled = false;
  let drawer: CashDrawerSessionDto | null = null;
  /** RCPT1-S5: the open drawer's newest kick did not open it. Read with the drawer, shown on the menu. */
  let kickFailed = false;
  let kickBusy = false;

  let scrim: HTMLElement | undefined;
  let step: Step = "loading";
  let counts: DrawerCounts = emptyCounts();
  /** One key per counted submission. The same counts resent with a reason keep it. */
  let submitKey = "";
  let busy = false;
  let errorText = "";
  let reasons: ActionReasonDto[] = [];
  let reasonId: string | undefined;
  /** The counts the committing submission carries, kept across the reason step. */
  let committedCounts: DrawerCounts = emptyCounts();
  let result: CashDrawerSessionDto | undefined;
  /** Set while the open flow runs for a cash payment: settled true when the drawer opens, false when the sheet closes first. */
  let settleTenderOpen: ((opened: boolean) => void) | undefined;
  /** CASH1-S3: the paid in, paid out or drop being recorded, its reasons and the one picked. */
  let moveSpec: DrawerMoveSpec | undefined;
  let moveReasons: ActionReasonDto[] = [];
  let moveReasonId: string | undefined;

  const entryVisible = (): boolean =>
    known && drawerEntryVisible({ has: deps.has, cashEnabled, drawerOpen: drawer !== null });

  // ---------------------------------------------------------------
  // Entry state
  // ---------------------------------------------------------------

  async function refreshEntry(): Promise<void> {
    if (!deps.has("drawer.open") && !deps.has("drawer.close")) {
      if (known || drawer !== null || cashEnabled) {
        known = false;
        drawer = null;
        cashEnabled = false;
        deps.onEntryChanged();
      }
      return;
    }
    const mine = sessionGeneration;
    const response = await getCurrentCashDrawer(deps.apiBaseUrl(), deps.tenantId());
    if (mine !== sessionGeneration) return;
    const wasVisible = entryVisible();
    if (response.status === 200 && !response.networkError) {
      known = true;
      cashEnabled = cashEnabledFrom(response.body);
      drawer = response.body.session ?? null;
      kickFailed = drawer !== null && drawerKickFailedFrom(response.body);
    } else {
      // Unreadable (no device bound, no permission, offline): the row stays hidden, nothing is guessed.
      known = false;
      cashEnabled = false;
      drawer = null;
    }
    if (entryVisible() !== wasVisible) deps.onEntryChanged();
  }

  // ---------------------------------------------------------------
  // The sheet
  // ---------------------------------------------------------------

  function ensureScrim(): HTMLElement {
    if (scrim !== undefined) return scrim;
    const root = el("div", "scrim scrim-top", undefined, SCRIM_ID);
    root.id = SCRIM_ID;
    root.hidden = true;
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeSheet();
    });
    document.body.append(root);
    scrim = root;
    return root;
  }

  function closeSheet(): void {
    const settle = settleTenderOpen;
    settleTenderOpen = undefined;
    generation += 1;
    scrim?.remove();
    scrim = undefined;
    step = "loading";
    counts = emptyCounts();
    committedCounts = emptyCounts();
    submitKey = "";
    busy = false;
    errorText = "";
    reasons = [];
    reasonId = undefined;
    result = undefined;
    settle?.(false);
    moveSpec = undefined;
    moveReasons = [];
    moveReasonId = undefined;
  }

  function goTo(next: Step): void {
    step = next;
    errorText = "";
    render();
  }

  function startCount(next: "open" | "count"): void {
    counts = emptyCounts();
    submitKey = crypto.randomUUID();
    goTo(next);
  }

  function shell(title: string, wide: boolean): { sheet: HTMLElement; body: HTMLElement } {
    const sheet = el(
      "div",
      wide ? "sheet cash-sheet cash-sheet-wide" : "sheet cash-sheet",
      undefined,
      "cash-drawer-sheet",
    );
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-labelledby", "cash-drawer-title");
    const header = el("div", "sheet-header");
    const row = el("div", "sheet-title-row");
    const titleEl = el("span", "sheet-title", title, "cash-drawer-title");
    titleEl.id = "cash-drawer-title";
    const x = button("btn-icon", "✕", "cash-drawer-close-x", () => closeSheet());
    x.setAttribute("aria-label", "Close");
    row.append(titleEl, x);
    header.append(row);
    sheet.append(header);
    const body = el("div", "cash-body");
    sheet.append(body);
    return { sheet, body };
  }

  function errorLine(): HTMLElement {
    const line = el("div", "cash-error", errorText, "cash-drawer-error");
    line.hidden = errorText === "";
    line.setAttribute("role", "alert");
    return line;
  }

  /** Tiles above a count or a result: label over value. */
  function tile(label: string, value: string, testId: string, className?: string): HTMLElement {
    const wrap = el("div", className);
    wrap.append(el("div", "l", label), el("div", "v", value, testId));
    return wrap;
  }

  function countBody(
    body: HTMLElement,
    label: string,
    submitLabel: string,
    submitTestId: string,
    onSubmit: () => void,
    onCancel: () => void,
  ): HTMLElement {
    const sum = el("div", "cash-sum");
    sum.append(tile(label, dollars(totalCents(counts)), "cash-drawer-counted", "big"));
    const grid = renderDenominationGrid({
      counts,
      busy,
      testPrefix: "cash-drawer",
      onChange: (next) => {
        counts = next;
        render();
      },
    });
    const foot = el("div", "sheet-footer cash-sheet-foot");
    const cancel = button("btn-secondary", "Cancel", "cash-drawer-cancel", onCancel);
    cancel.disabled = busy;
    const submit = button("btn-primary", submitLabel, submitTestId, onSubmit);
    submit.disabled = busy || totalCents(counts) === 0;
    foot.append(cancel, submit);
    body.append(sum, errorLine(), grid, foot);
    return submit;
  }

  function render(): void {
    const root = ensureScrim();
    root.replaceChildren();
    let focusTarget: HTMLElement | undefined;
    if (step === "loading") {
      const { sheet, body } = shell("Cash drawer", false);
      body.append(el("div", "cash-loading", "Loading", "cash-drawer-loading"));
      root.append(sheet);
    } else if (step === "open") {
      const { sheet, body } = shell("Open drawer", true);
      focusTarget = countBody(
        body,
        "Counted float",
        "Open drawer",
        "cash-drawer-open-submit",
        () => void submitOpen(),
        () => closeSheet(),
      );
      root.append(sheet);
    } else if (step === "menu" && drawer !== null) {
      const { sheet, body } = shell("Cash drawer", false);
      const status = el("div", "cash-status", undefined, "cash-drawer-status");
      const pair = (label: string, value: string, testId: string): HTMLElement => {
        const span = el("span", undefined, `${label} `);
        span.append(el("b", undefined, value, testId));
        return span;
      };
      status.append(
        pair("Opened by", drawer.custodian.name, "cash-drawer-custodian"),
        pair("Since", clockTime(drawer.openedAt), "cash-drawer-since"),
        pair(
          "Float",
          floatLine(drawer.openingFloatCents, drawer.standardFloatCents),
          "cash-drawer-float",
        ),
      );
      body.append(status);
      if (kickFailed) {
        const kick = el("div", "cash-kick-failed", undefined, "cash-drawer-kick-failed");
        kick.append(el("b", undefined, "Drawer did not open", "cash-drawer-kick-failed-text"));
        if (deps.has("drawer.open")) {
          const again = button(
            "btn-secondary",
            "Open drawer",
            "cash-drawer-kick-again",
            () => void openByHand(),
          );
          again.disabled = kickBusy;
          kick.append(again);
        }
        body.append(kick);
      }
      const tiles = visibleMoves(deps.has, true);
      if (tiles.length > 0) {
        const menu = el("div", "cash-menu", undefined, "cash-drawer-moves");
        for (const spec of tiles) {
          const tileButton = button(
            "",
            spec.label,
            `cash-drawer-move-${spec.kind}`,
            () => void startMove(spec),
          );
          tileButton.append(el("small", undefined, spec.hint));
          menu.append(tileButton);
        }
        body.append(menu);
      }
      const foot = el("div", "sheet-footer");
      if (deps.has("drawer.close")) {
        const close = button(
          "btn-secondary btn-full",
          "Close drawer",
          "cash-drawer-menu-close",
          () => startCount("count"),
        );
        foot.append(close);
        focusTarget = close;
      } else {
        const done = button("btn-secondary btn-full", "Done", "cash-drawer-done", () =>
          closeSheet(),
        );
        foot.append(done);
        focusTarget = done;
      }
      body.append(foot);
      root.append(sheet);
    } else if (step === "count" && drawer !== null) {
      const { sheet, body } = shell(countTitle(drawer.recountUsed), true);
      focusTarget = countBody(
        body,
        "Counted",
        "Submit count",
        "cash-drawer-count-submit",
        () => void submitCount(),
        () => goTo("menu"),
      );
      root.append(sheet);
    } else if (step === "move" && drawer !== null && moveSpec !== undefined) {
      const spec = moveSpec;
      const { sheet, body } = shell(spec.label, false);
      body.append(el("div", "cash-field-label", "Reason"));
      const grid = el("div", "cash-reasons", undefined, "cash-drawer-move-reasons");
      if (moveReasons.length === 0) {
        grid.append(el("span", "cash-na", "No reasons set up yet. A manager can add them in Settings.", "cash-drawer-move-reasons-empty"));
      }
      for (const reason of moveReasons) {
        const chip = button(
          reason.id === moveReasonId ? "selected" : "",
          reason.label,
          "cash-drawer-move-reason",
          () => {
            moveReasonId = reason.id;
            render();
          },
        );
        chip.setAttribute("aria-pressed", reason.id === moveReasonId ? "true" : "false");
        chip.disabled = busy;
        grid.append(chip);
      }
      body.append(grid, el("div", "cash-field-label", "Amount"));
      const amount = button("cash-amount-row", "", "cash-drawer-move-amount", () =>
        goTo("movecount"),
      );
      amount.append(
        el("b", undefined, dollars(totalCents(counts)), "cash-drawer-move-total"),
        el("span", "link", "Count cash"),
      );
      amount.disabled = busy;
      body.append(amount, errorLine());
      const foot = el("div", "sheet-footer cash-sheet-foot");
      const cancel = button("btn-secondary", "Cancel", "cash-drawer-cancel", () => goTo("menu"));
      cancel.disabled = busy;
      const save = button(
        "btn-primary",
        spec.saveLabel,
        "cash-drawer-move-save",
        () => void submitMove(),
      );
      save.disabled = busy || !canSaveMove({ counts, reasonId: moveReasonId });
      foot.append(cancel, save);
      body.append(foot);
      focusTarget = grid.querySelector<HTMLElement>("button") ?? amount;
      root.append(sheet);
    } else if (step === "movecount" && moveSpec !== undefined) {
      const { sheet, body } = shell(`${moveSpec.label}: count the cash`, true);
      focusTarget = countBody(
        body,
        "Counted",
        "Done",
        "cash-drawer-move-count-done",
        () => goTo("move"),
        () => goTo("move"),
      );
      root.append(sheet);
    } else if (step === "limit" && result !== undefined) {
      const { sheet, body } = shell("Close drawer", false);
      // The open drawer's view carries Expected (for drawer.view_expected only) but no variance yet: counted minus expected.
      const expected = result.expectedCents;
      const figures = typeof expected === "number";
      const headline = figures
        ? overShort(totalCents(committedCounts) - expected).headline
        : "Outside the limit";
      const resultBlock = el("div", "cash-result");
      resultBlock.append(el("div", "cash-result-value", headline, "cash-drawer-limit-headline"));
      body.append(resultBlock);
      const foot = el("div", "sheet-footer cash-sheet-foot");
      foot.append(button("btn-secondary", "Cancel", "cash-drawer-cancel", () => closeSheet()));
      const recount = button("btn-primary", "Recount", "cash-drawer-recount", () =>
        startCount("count"),
      );
      foot.append(recount);
      focusTarget = recount;
      body.append(foot);
      root.append(sheet);
    } else if (step === "approve") {
      const { sheet, body } = shell("Close drawer", false);
      const resultBlock = el("div", "cash-result");
      resultBlock.append(
        el("div", "cash-result-value", "Outside the limit", "cash-drawer-approve-headline"),
      );
      body.append(resultBlock, el("div", "cash-field-label", "Reason"));
      const grid = el("div", "cash-reasons", undefined, "cash-drawer-reasons");
      if (reasons.length === 0) {
        grid.append(el("span", "cash-na", "No reasons set up yet. A manager can add them in Settings.", "cash-drawer-reasons-empty"));
      }
      for (const reason of reasons) {
        const chip = button(
          reason.id === reasonId ? "selected" : "",
          reason.label,
          "cash-drawer-reason",
          () => {
            reasonId = reason.id;
            render();
          },
        );
        chip.setAttribute("aria-pressed", reason.id === reasonId ? "true" : "false");
        chip.disabled = busy;
        grid.append(chip);
      }
      body.append(grid);
      const approver = deps.holdsOutright("drawer.approve");
      if (!approver) {
        body.append(
          el("div", "cash-approve", "Manager approval needed", "cash-drawer-approve-notice"),
        );
      }
      body.append(errorLine());
      const foot = el("div", "sheet-footer cash-sheet-foot");
      const cancel = button("btn-secondary", "Cancel", "cash-drawer-cancel", () => closeSheet());
      cancel.disabled = busy;
      const submit = button(
        "btn-primary",
        approver ? "Close drawer" : "Manager approve",
        "cash-drawer-approve-submit",
        () => void submitApproved(),
      );
      submit.disabled = busy || reasonId === undefined;
      foot.append(cancel, submit);
      body.append(foot);
      focusTarget = grid.querySelector<HTMLElement>("button") ?? undefined;
      root.append(sheet);
    } else if (step === "result" && result !== undefined) {
      const { sheet, body } = shell("Close drawer", false);
      const figures =
        typeof result.varianceCents === "number" && typeof result.expectedCents === "number";
      const block = el("div", "cash-result");
      if (figures && typeof result.varianceCents === "number") {
        const view = overShort(result.varianceCents);
        const value = el(
          "div",
          `cash-result-value cash-result-${view.kind}`,
          view.headline,
          "cash-drawer-result-headline",
        );
        block.append(value);
        if (typeof result.toleranceCents === "number") {
          block.append(el("div", "cash-result-sub", `Limit is ${dollars(result.toleranceCents)}`));
        }
        body.append(block);
        const pairs = el("div", "cash-pairs");
        pairs.append(
          tile("Counted", dollars(result.countedCents ?? 0), "cash-drawer-result-counted"),
          tile("Expected", dollars(result.expectedCents ?? 0), "cash-drawer-result-expected"),
          tile("Difference", view.difference, "cash-drawer-result-difference"),
        );
        body.append(pairs);
      } else {
        block.append(
          el("div", "cash-result-value", "Drawer closed", "cash-drawer-result-headline"),
        );
        body.append(block);
        const pairs = el("div", "cash-pairs cash-pairs-one");
        pairs.append(
          tile("Counted", dollars(result.countedCents ?? 0), "cash-drawer-result-counted"),
        );
        body.append(pairs);
      }
      const foot = el("div", "sheet-footer");
      const done = button("btn-primary btn-full", "Done", "cash-drawer-done", () => closeSheet());
      foot.append(done);
      body.append(foot);
      focusTarget = done;
      root.append(sheet);
    }
    root.hidden = false;
    focusTarget?.focus();
  }

  // ---------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------

  async function openSheet(): Promise<void> {
    if (!deps.has("drawer.open") && !deps.has("drawer.close")) return;
    closeSheet();
    step = "loading";
    render();
    const mine = generation;
    const response = await getCurrentCashDrawer(deps.apiBaseUrl(), deps.tenantId());
    if (mine !== generation) return;
    if (response.status !== 200 || response.networkError) {
      closeSheet();
      deps.toast(messageOf(response.body));
      return;
    }
    known = true;
    cashEnabled = cashEnabledFrom(response.body);
    drawer = response.body.session ?? null;
    kickFailed = drawer !== null && drawerKickFailedFrom(response.body);
    if (drawer === null) {
      if (!deps.has("drawer.open")) {
        closeSheet();
        deps.toast("No drawer is open on this device.");
        return;
      }
      startCount("open");
    } else {
      goTo("menu");
    }
    deps.onEntryChanged();
  }

  /** RCPT1-S5: "Open drawer" after a kick that did not open it. One key per tap, so a double tap is one kick. */
  async function openByHand(): Promise<void> {
    if (kickBusy || !deps.has("drawer.open")) return;
    kickBusy = true;
    const mine = generation;
    const sent = await kickCashDrawer(deps.apiBaseUrl(), deps.tenantId(), {
      idempotencyKey: crypto.randomUUID(),
    });
    kickBusy = false;
    if (mine !== generation) return;
    if (sent.status !== 200 && sent.status !== 201) {
      deps.toast(messageOf(sent.body));
      render();
      return;
    }
    // The new kick is pending; the failure line clears until a newer read says otherwise.
    kickFailed = false;
    render();
  }

  async function startMove(spec: DrawerMoveSpec): Promise<void> {
    if (!deps.has(spec.permission) || drawer === null) return;
    moveSpec = spec;
    moveReasons = [];
    moveReasonId = undefined;
    counts = emptyCounts();
    submitKey = crypto.randomUUID();
    goTo("move");
    const mine = generation;
    const response = await listActionReasons(deps.apiBaseUrl(), deps.tenantId(), {
      kind: spec.reasonKind,
      activeOnly: true,
    });
    if (mine !== generation) return;
    if (response.status === 200 && Array.isArray(response.body.actionReasons)) {
      moveReasons = response.body.actionReasons;
    } else {
      errorText = messageOf(response.body);
    }
    render();
  }

  async function submitMove(): Promise<void> {
    const spec = moveSpec;
    if (busy || drawer === null || spec === undefined) return;
    const body = moveBody({ counts, reasonId: moveReasonId }, submitKey);
    if (body === null) return;
    const session = drawer;
    busy = true;
    errorText = "";
    render();
    const mine = generation;
    const send = (approval?: { approverUserId: string; approverPin: string }) =>
      postCashDrawerMove(deps.apiBaseUrl(), deps.tenantId(), session.id, spec.kind, {
        ...body,
        ...approval,
      });
    let response: Awaited<ReturnType<typeof send>>;
    if (spec.kind === "paid-out") {
      // A paid out over the location's threshold needs a manager: the server answers approval_required and the existing approval
      // sheet takes their PIN. The same counts, reason and key are resent.
      const gated = await deps.requestApproval(
        "Pay out cash",
        async (approval) => {
          const attempt = await send(approval);
          if (
            attempt.status === 403 &&
            attempt.body.error === "approval_required" &&
            scrim !== undefined
          ) {
            scrim.hidden = true;
          }
          return attempt;
        },
        () => {
          deps.showOrigin();
          if (scrim !== undefined) scrim.hidden = false;
        },
      );
      if (mine !== generation) return;
      busy = false;
      if (scrim !== undefined) scrim.hidden = false;
      if (!gated.canceled) deps.showOrigin();
      if (gated.canceled) {
        render();
        return;
      }
      response = gated.result;
    } else {
      response = await send();
      if (mine !== generation) return;
      busy = false;
    }
    const outcome = classifyMoveResponse({
      status: response.status,
      networkError: response.networkError,
      body: response.body,
    });
    if (outcome.kind === "saved" && response.body.session !== undefined) {
      drawer = response.body.session;
      counts = emptyCounts();
      goTo("menu");
      return;
    }
    errorText = outcome.kind === "error" ? outcome.message : messageOf(response.body);
    render();
  }

  async function submitOpen(): Promise<void> {
    if (busy || totalCents(counts) === 0) return;
    busy = true;
    errorText = "";
    render();
    const mine = generation;
    const response = await openCashDrawer(deps.apiBaseUrl(), deps.tenantId(), {
      idempotencyKey: submitKey,
      amountCents: totalCents(counts),
      denominations: breakdownOf(counts),
    });
    if (mine !== generation) return;
    busy = false;
    if (
      (response.status === 200 || response.status === 201) &&
      response.body.session !== undefined
    ) {
      drawer = response.body.session;
      known = true;
      counts = emptyCounts();
      deps.onEntryChanged();
      const settle = settleTenderOpen;
      if (settle !== undefined) {
        // Opened for a cash payment: back to Pay, no drawer menu in between.
        settleTenderOpen = undefined;
        closeSheet();
        settle(true);
        return;
      }
      goTo("menu");
      return;
    }
    errorText = messageOf(response.body);
    render();
  }

  function closeBody(extra: { reasonId?: string }): {
    idempotencyKey: string;
    amountCents: number;
    denominations: Record<string, number>;
    reasonId?: string;
  } {
    return {
      idempotencyKey: submitKey,
      amountCents: totalCents(committedCounts),
      denominations: breakdownOf(committedCounts),
      ...extra,
    };
  }

  async function submitCount(): Promise<void> {
    if (busy || drawer === null || totalCents(counts) === 0) return;
    busy = true;
    errorText = "";
    committedCounts = counts;
    render();
    const mine = generation;
    const response = await closeCashDrawer(
      deps.apiBaseUrl(),
      deps.tenantId(),
      drawer.id,
      closeBody({}),
    );
    if (mine !== generation) return;
    busy = false;
    const outcome = classifyCloseResponse({
      status: response.status,
      networkError: response.networkError,
      body: response.body,
      hadReason: false,
    });
    if (outcome.kind === "closed" || outcome.kind === "recount_required") {
      result = response.body.session;
      if (result !== undefined) drawer = outcome.kind === "closed" ? null : result;
      if (outcome.kind === "closed") deps.onEntryChanged();
      goTo(outcome.kind === "closed" ? "result" : "limit");
      return;
    }
    if (outcome.kind === "needs_approval") {
      await openApproveStep();
      return;
    }
    errorText = outcome.message;
    render();
  }

  async function openApproveStep(): Promise<void> {
    reasonId = undefined;
    reasons = [];
    step = "approve";
    errorText = "";
    render();
    const mine = generation;
    const response = await listActionReasons(deps.apiBaseUrl(), deps.tenantId(), {
      kind: "drawer_variance",
      activeOnly: true,
    });
    if (mine !== generation) return;
    if (response.status === 200 && Array.isArray(response.body.actionReasons)) {
      reasons = response.body.actionReasons;
    } else {
      errorText = messageOf(response.body);
    }
    render();
  }

  async function submitApproved(): Promise<void> {
    const picked = reasonId;
    if (busy || drawer === null || picked === undefined) return;
    const session = drawer;
    busy = true;
    errorText = "";
    render();
    const mine = generation;
    const gated = await deps.requestApproval(
      "Close the cash drawer",
      async (approval) => {
        const attempt = await closeCashDrawer(deps.apiBaseUrl(), deps.tenantId(), session.id, {
          ...closeBody({ reasonId: picked }),
          ...approval,
        });
        // The approval screens replace this sheet while they are up; the sheet comes back after.
        if (
          attempt.status === 403 &&
          attempt.body.error === "approval_required" &&
          scrim !== undefined
        ) {
          scrim.hidden = true;
        }
        return attempt;
      },
      () => {
        deps.showOrigin();
        if (scrim !== undefined) scrim.hidden = false;
      },
    );
    if (mine !== generation) return;
    busy = false;
    if (scrim !== undefined) scrim.hidden = false;
    if (!gated.canceled) deps.showOrigin();
    if (gated.canceled) {
      render();
      return;
    }
    const outcome = classifyCloseResponse({
      status: gated.result.status,
      networkError: gated.result.networkError,
      body: gated.result.body,
      hadReason: true,
    });
    if (outcome.kind === "closed" && gated.result.body.session !== undefined) {
      result = gated.result.body.session;
      drawer = null;
      deps.onEntryChanged();
      goTo("result");
      return;
    }
    errorText = outcome.kind === "error" ? outcome.message : messageOf(gated.result.body);
    render();
  }

  async function tenderState(): Promise<{
    cashEnabled: boolean;
    drawerOpen: boolean;
    canOpen: boolean;
  } | null> {
    if (!deps.has("drawer.open")) {
      // CASH1-S2-U1-F1: no drawer.open, so the drawer screens' own read is not theirs. The pay-gated read answers only the
      // switch and whether a drawer is open here; the controller's drawer state is left alone.
      const mineAlone = sessionGeneration;
      const light = await getCashTenderState(deps.apiBaseUrl(), deps.tenantId());
      if (mineAlone !== sessionGeneration) return null;
      if (light.status !== 200 || light.networkError) return null;
      return {
        cashEnabled: cashEnabledFrom(light.body),
        drawerOpen: light.body.drawerOpen === true,
        canOpen: false,
      };
    }
    const mine = sessionGeneration;
    const response = await getCurrentCashDrawer(deps.apiBaseUrl(), deps.tenantId());
    if (mine !== sessionGeneration) return null;
    if (response.status !== 200 || response.networkError) return null;
    drawer = response.body.session ?? null;
    cashEnabled = cashEnabledFrom(response.body);
    known = true;
    return { cashEnabled, drawerOpen: drawer !== null, canOpen: true };
  }

  async function ensureOpenForTender(options?: { refund?: boolean }): Promise<boolean> {
    const state = await tenderState();
    if (state === null) {
      deps.toast("Could not read the cash drawer. Try again.");
      return false;
    }
    // A cash sale needs the switch on. A cash refund does not (turning cash off never strands an open drawer), only a drawer.
    if (options?.refund === true && state.drawerOpen) return true;
    if (!state.cashEnabled) {
      deps.toast("This location is not taking cash.");
      return false;
    }
    if (state.drawerOpen) return true;
    if (!state.canOpen) {
      deps.toast("Drawer closed.");
      return false;
    }
    closeSheet();
    return await new Promise<boolean>((resolve) => {
      settleTenderOpen = resolve;
      startCount("open");
    });
  }

  function reset(): void {
    sessionGeneration += 1;
    closeSheet();
    known = false;
    cashEnabled = false;
    drawer = null;
  }

  return {
    refreshEntry,
    entryVisible,
    openSheet,
    tenderState,
    ensureOpenForTender,
    reset,
    isOpen: () => scrim !== undefined && !scrim.hidden,
  };
}
